import React, { useEffect, useState } from 'react';
import { Box, Divider, IconButton, Popover, TextField, Button, Tooltip } from '@mui/material';
import type { Editor } from '@tiptap/core';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import SubscriptIcon from '@mui/icons-material/Subscript';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import TableChartIcon from '@mui/icons-material/TableChart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

/**
 * Track A, Phase 4+5 of the Smart Reporting rebuild — the Word-like toolbar
 * Kevin himself described (12 Aug call, 31:00–31:20: "If you want to add a
 * table, then you can click on the table... If you want to add a heading,
 * you can add a heading... It's kind of like a word file"). The underlying
 * TipTap extensions (CustomHeader, bold/italic/underline, lists, CustomTable)
 * already exist in reportExtension.ts — this is the missing UI to invoke
 * them. Nothing here is a new extension; every button below is a standard
 * TipTap chain command.
 *
 * Expanded 24 Aug — same finding again: TextAlign, Color, Highlight, Link,
 * Subscript/Superscript were ALSO already registered extensions with no
 * toolbar (same class of gap as headings/tables). No third-party library
 * needed for any of this — every capability here already existed dormant in
 * reportExtension.ts's extensions array before this file was ever written.
 */

const TEXT_COLORS = ['#e7eef7', '#fb7185', '#f7a35a', '#4ade80', '#38bdf8', '#a78bfa', '#000000'];
const HIGHLIGHT_COLORS = ['#fff59d', '#a7f3d0', '#bfdbfe', '#fbcfe8', '#fde68a'];

interface TemplateEditorToolbarProps {
  editor: Editor | null;
}

interface ToolbarButtonProps {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ title, active, disabled, onClick, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={theme => ({
          borderRadius: 1,
          color: active ? theme.palette.cardiac?.teal || '#2dd4bf' : 'inherit',
          backgroundColor: active ? 'rgba(45, 212, 191, 0.12)' : 'transparent',
        })}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

interface ColorSwatchButtonProps {
  title: string;
  icon: React.ReactNode;
  colors: string[];
  active: boolean;
  onPick: (color: string) => void;
  onClear: () => void;
}

/** Shared popover-of-swatches control for text color and highlight. */
const ColorSwatchButton: React.FC<ColorSwatchButtonProps> = ({
  title,
  icon,
  colors,
  active,
  onPick,
  onClear,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <ToolbarButton title={title} active={active} onClick={e => setAnchorEl(e.currentTarget)}>
        {icon}
      </ToolbarButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ display: 'flex', gap: 0.75, p: 1 }}>
          {colors.map(color => (
            <Box
              key={color}
              onClick={() => {
                onPick(color);
                setAnchorEl(null);
              }}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: color,
                border: '1px solid rgba(0,0,0,0.2)',
                cursor: 'pointer',
              }}
            />
          ))}
          <Box
            onClick={() => {
              onClear();
              setAnchorEl(null);
            }}
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              border: '1px solid #999',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
            }}
          >
            ✕
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const LinkButton: React.FC<{ editor: Editor }> = ({ editor }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [url, setUrl] = useState('');
  const active = editor.isActive('link');

  return (
    <>
      <ToolbarButton
        title="Insert link"
        active={active}
        onClick={e => {
          setUrl(editor.getAttributes('link')?.href || '');
          setAnchorEl(e.currentTarget);
        }}
      >
        <LinkIcon fontSize="small" />
      </ToolbarButton>
      {active && (
        <ToolbarButton
          title="Remove link"
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          <LinkOffIcon fontSize="small" />
        </ToolbarButton>
      )}
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ display: 'flex', gap: 1, p: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="https://…"
            value={url}
            autoFocus
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && url.trim()) {
                editor.chain().focus().setLink({ href: url.trim() }).run();
                setAnchorEl(null);
              }
            }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={!url.trim()}
            onClick={() => {
              editor.chain().focus().setLink({ href: url.trim() }).run();
              setAnchorEl(null);
            }}
          >
            Set
          </Button>
        </Box>
      </Popover>
    </>
  );
};

const HeadingButton: React.FC<{ editor: Editor; level: 1 | 2 | 3 }> = ({ editor, level }) => (
  <ToolbarButton
    title={`Heading ${level}`}
    active={editor.isActive('heading', { level })}
    onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
  >
    <Box component="span" sx={{ fontSize: 13, fontWeight: 700, width: 18, textAlign: 'center' }}>
      H{level}
    </Box>
  </ToolbarButton>
);

const TemplateEditorToolbar: React.FC<TemplateEditorToolbarProps> = ({ editor }) => {
  // TipTap's `editor` instance mutates in place — its own methods (isActive)
  // don't trigger a React re-render on selection/content change. Force one
  // on every transaction so active-state highlighting (bold/heading/etc.)
  // and the "inside a table" toolbar stay in sync with the cursor.
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (!editor) {
      return;
    }
    const rerender = () => forceRender(n => n + 1);
    editor.on('transaction', rerender);
    editor.on('selectionUpdate', rerender);
    return () => {
      editor.off('transaction', rerender);
      editor.off('selectionUpdate', rerender);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  const insideTable = editor.isActive('table');

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.5,
        p: 0.75,
        borderRadius: 1,
        border: `1px solid ${theme.palette.cardiac?.line || '#2a3a4a'}`,
        backgroundColor: theme.palette.cardiac?.surface2 || 'rgba(255,255,255,0.03)',
        mb: 1,
      })}
    >
      <HeadingButton editor={editor} level={1} />
      <HeadingButton editor={editor} level={2} />
      <HeadingButton editor={editor} level={3} />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <ToolbarButton
        title="Bold"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <FormatBoldIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <FormatItalicIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <FormatUnderlinedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <FormatStrikethroughIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Subscript"
        active={editor.isActive('subscript')}
        onClick={() => editor.chain().focus().toggleSubscript().run()}
      >
        <SubscriptIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Superscript"
        active={editor.isActive('superscript')}
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
      >
        <SuperscriptIcon fontSize="small" />
      </ToolbarButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <ColorSwatchButton
        title="Text color"
        icon={<FormatColorTextIcon fontSize="small" />}
        colors={TEXT_COLORS}
        active={editor.isActive('textStyle')}
        onPick={color => editor.chain().focus().setColor(color).run()}
        onClear={() => editor.chain().focus().unsetColor().run()}
      />
      <ColorSwatchButton
        title="Highlight"
        icon={<FormatColorFillIcon fontSize="small" />}
        colors={HIGHLIGHT_COLORS}
        active={editor.isActive('highlight')}
        onPick={color => editor.chain().focus().toggleHighlight({ color }).run()}
        onClear={() => editor.chain().focus().unsetHighlight().run()}
      />
      <LinkButton editor={editor} />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <ToolbarButton
        title="Align left"
        active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <FormatAlignLeftIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Align center"
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <FormatAlignCenterIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Align right"
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <FormatAlignRightIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Justify"
        active={editor.isActive({ textAlign: 'justify' })}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
      >
        <FormatAlignJustifyIcon fontSize="small" />
      </ToolbarButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <ToolbarButton
        title="Bullet list"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <FormatListBulletedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <FormatListNumberedIcon fontSize="small" />
      </ToolbarButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <ToolbarButton
        title="Insert table"
        disabled={insideTable}
        onClick={() => {
          // insertTable() alone produces borderless markup — every other
          // table in this app (demographic/measurement tables, imported
          // .docx/.html) gets its grid from the `table-bordered` class
          // (see uploadTemplate.tsx's docx/html import pipeline). Without
          // it, a toolbar-inserted table is invisible, which is exactly
          // what happened here.
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
          const { selection } = editor.state;
          editor
            .chain()
            .command(({ tr, state }) => {
              state.doc.nodesBetween(
                Math.max(0, selection.from - 1),
                Math.min(state.doc.content.size, selection.from + 1),
                (node, pos) => {
                  if (node.type.name === 'table') {
                    tr.setNodeMarkup(pos, undefined, {
                      ...node.attrs,
                      additionalClass: 'table-bordered',
                    });
                    return false;
                  }
                  return true;
                }
              );
              return true;
            })
            .run();
        }}
      >
        <TableChartIcon fontSize="small" />
      </ToolbarButton>

      {insideTable && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <ToolbarButton
            title="Add row after"
            onClick={() => editor.chain().focus().addRowAfter().run()}
          >
            <AddIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Delete row"
            onClick={() => editor.chain().focus().deleteRow().run()}
          >
            <RemoveIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            title="Add column after"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AddIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
            </Box>
          </ToolbarButton>
          <ToolbarButton
            title="Delete column"
            onClick={() => editor.chain().focus().deleteColumn().run()}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RemoveIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
            </Box>
          </ToolbarButton>
          <ToolbarButton
            title="Delete table"
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            <DeleteOutlineIcon fontSize="small" />
          </ToolbarButton>
        </>
      )}
    </Box>
  );
};

export default TemplateEditorToolbar;
